import React from "react";
import Layout from "../Layout";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { FaWhatsapp, FaFacebook, FaInstagram } from "react-icons/fa";
import { MdOutlineEmail } from "react-icons/md";
import { LuPhone } from "react-icons/lu";
import FormContactUs from "./FormContactUs";

function ContactUs() {
  const { t } = useTranslation();
  
  return (
    <Layout title={t("pages.contact")}>
      {/* Hero Section */}
      <section className="py-8 md:py-12 lg:py-16 relative overflow-hidden">
        {/* خلفية متدرجة */}
        <div className="absolute inset-0 bg-gradient-to-br from-MainBlue/5 via-blue-600/3 to-MainBlue/5"></div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-MainBlue/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-600/5 rounded-full blur-3xl"></div>

        <div className="relative max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-10 md:mb-14"
          >
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4">
              <span className="bg-gradient-to-r from-MainBlue via-blue-600 to-MainBlue bg-clip-text text-transparent">
                {t("pages.contact")}
              </span>
            </h1>
            <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto">
              {t("contact.sendMassageParagraph")}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-8 md:py-12 lg:py-16 bg-gradient-to-br from-muted via-muted/50 to-muted/30">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
            {/* Left: Form */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-card rounded-xl md:rounded-2xl border-2 border-border/50 shadow-lg hover:shadow-xl transition-all duration-300 p-6 md:p-8"
            >
              <div className="mb-6">
                <h3 className="text-2xl md:text-3xl font-bold mb-2 text-foreground">
                  {t("contact.sendMessage")}
                </h3>
                <p className="text-sm md:text-base text-muted-foreground">
                  {t("contact.sendMassageParagraph")}
                </p>
              </div>
              <FormContactUs />
            </motion.div>

            {/* Right: Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-card rounded-xl md:rounded-2xl border-2 border-border/50 shadow-lg hover:shadow-xl transition-all duration-300 p-6 md:p-8 flex flex-col justify-between"
            >
              <div>
                <h3 className="text-2xl md:text-3xl font-bold mb-6 text-foreground">
                  {t("contact.ReachOutDirectly")}
                </h3>
                <ul className="space-y-4 md:space-y-5">
                  <motion.li
                    whileHover={{ x: 5 }}
                    className="group"
                  >
                    <a
                      className="flex items-center gap-4 p-3 rounded-lg hover:bg-muted transition-colors"
                      href="tel:+971528401112"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <div className="p-2 bg-MainBlue/10 group-hover:bg-MainBlue/20 rounded-lg transition-colors">
                        <LuPhone className="text-MainBlue text-xl md:text-2xl" />
                      </div>
                      <span dir="ltr" className="text-base md:text-lg text-foreground font-medium">
                        +971 52 840 1112
                      </span>
                    </a>
                  </motion.li>
                  <motion.li
                    whileHover={{ x: 5 }}
                    className="group"
                  >
                    <a
                      className="flex items-center gap-4 p-3 rounded-lg hover:bg-muted transition-colors"
                      href="mailto:info@twhmenu.com"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <div className="p-2 bg-MainBlue/10 group-hover:bg-MainBlue/20 rounded-lg transition-colors">
                        <MdOutlineEmail className="text-MainBlue text-xl md:text-2xl" />
                      </div>
                      <span className="text-base md:text-lg text-foreground font-medium">
                        info@twhmenu.com
                      </span>
                    </a>
                  </motion.li>
                  <motion.li
                    whileHover={{ x: 5 }}
                    className="group"
                  >
                    <a
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-4 p-3 rounded-lg hover:bg-muted transition-colors"
                      href="https://wa.me///+971528401112?text=Hello%21+I+found+TWHMENU.com+and+I+want+to+create+digital+menu"
                    >
                      <div className="p-2 bg-green-500/10 group-hover:bg-green-500/20 rounded-lg transition-colors">
                        <FaWhatsapp className="text-green-500 text-xl md:text-2xl" />
                      </div>
                      <span dir="ltr" className="text-base md:text-lg text-foreground font-medium">
                        +971 52 840 1112
                      </span>
                    </a>
                  </motion.li>
                </ul>
              </div>

              {/* Social Media */}
              <div className="mt-8 pt-6 border-t border-border/50">
                <h3 className="text-lg md:text-xl font-semibold mb-4 text-foreground">
                  {t("contact.ConnectWithUs")}
                </h3>
                <div className="flex gap-4">
                  <motion.a
                    whileHover={{ scale: 1.2, y: -3 }}
                    whileTap={{ scale: 0.9 }}
                    href="https://www.facebook.com/share/1FQEcag2w5/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-3 rounded-full bg-blue-500/10 hover:bg-blue-500/20 text-blue-600 transition-all"
                  >
                    <FaFacebook className="text-xl md:text-2xl" />
                  </motion.a>
                  <motion.a
                    whileHover={{ scale: 1.2, y: -3 }}
                    whileTap={{ scale: 0.9 }}
                    href="https://www.instagram.com/twh.tech?utm_source=ig_web_button_share_sheet&igsh=ZDNlZDc0MzIxNw=="
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-3 rounded-full bg-pink-500/10 hover:bg-pink-500/20 text-pink-600 transition-all"
                  >
                    <FaInstagram className="text-xl md:text-2xl" />
                  </motion.a>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </Layout>
  );
}

export default ContactUs;
